import SingleButtonDropdown from '@rio-cloud/rio-uikit/SingleButtonDropdown';

export const PAGE_SIZE_OPTIONS = [20, 40, 60];

const PAGINATION_SIZE = 5;

interface PaginationControl {
    key: string;
    label: string;
    page: number;
    active: boolean;
}

interface PageSizeOption {
    value: number;
    isSelected: boolean;
    onSelect: () => void;
}

export const getPaginationControls = (page: number, totalPages: number): PaginationControl[] => {
    const controls: PaginationControl[] = [];
    const half = Math.floor(PAGINATION_SIZE / 2);
    const ceilHalf = Math.ceil(PAGINATION_SIZE / 2);

    if (page > ceilHalf && totalPages > PAGINATION_SIZE) {
        controls.push({ key: 'first', label: '\u00AB\u00AB', page: 1, active: false });
    }
    if (page > 1) {
        controls.push({ key: 'prev', label: '\u00AB', page: page - 1, active: false });
    }

    let start: number;
    let end: number;
    if (totalPages <= PAGINATION_SIZE) {
        start = 1;
        end = totalPages;
    } else if (page < ceilHalf) {
        start = 1;
        end = PAGINATION_SIZE;
    } else if (page > totalPages - half) {
        start = totalPages - PAGINATION_SIZE + 1;
        end = totalPages;
    } else {
        start = page - half;
        end = page + half;
    }
    for (let pageNumber = start; pageNumber <= end; pageNumber++) {
        controls.push({
            key: `page-${pageNumber}`,
            label: `${pageNumber}`,
            page: pageNumber,
            active: pageNumber === page,
        });
    }

    if (totalPages > page) {
        controls.push({ key: 'next', label: '\u00BB', page: page + 1, active: false });
    }
    if (totalPages - half > page && totalPages > PAGINATION_SIZE) {
        controls.push({ key: 'last', label: '\u00BB\u00BB', page: totalPages, active: false });
    }
    return controls;
};

interface PaginationProps {
    pageCount: number;
    selectedPage: number;
    onPageSelect: (page: number) => void;
    pageOptions: PageSizeOption[];
}

const Pagination = ({ pageCount, selectedPage, onPageSelect, pageOptions }: PaginationProps) => {
    const selectedOption = pageOptions.find((option) => option.isSelected) ?? pageOptions[0];
    const dropdownItems = pageOptions.map((option) => ({
        value: <span>{option.value}</span>,
        active: option.isSelected,
        onSelect: option.onSelect,
    }));
    const controls = getPaginationControls(selectedPage, pageCount);

    return (
        <div
            className="Pagination display-flex align-items-center margin-top-15 margin-bottom-20"
            data-testid="pagination"
        >
            <SingleButtonDropdown title={`${selectedOption?.value ?? ''}`} items={dropdownItems} dropup />
            <ul className="pagination" style={{ margin: 'auto' }}>
                {controls.map((control) => {
                    const testId =
                        control.key === 'next' ? 'next-page' : control.key === 'prev' ? 'prev-page' : undefined;
                    return (
                        <li
                            key={control.key}
                            className={control.active ? 'active' : ''}
                            data-testid={control.active ? 'active-page' : undefined}
                        >
                            <a
                                role="button"
                                tabIndex={0}
                                onClick={() => onPageSelect(control.page)}
                                data-testid={testId}
                            >
                                {control.label}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default Pagination;
